"use client";

import { useMemo } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import { shown } from "@/components/tools/calculus/format";
import { FunctionGraph } from "@/components/tools/calculus/function-graph";
import { MathView } from "@/components/tools/calculus/math-view";
import type {
  DEFINITE_INTEGRAL_CALCULATOR_STRINGS,
  IntegralView,
} from "@/components/tools/definite-integral-calculator";
import type { PlaneScales } from "@/components/tools/math/coordinate-plane";
import type { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { plotRange } from "@/lib/tool-kit/calculus/integral-report";
import type { IntegralWarning } from "@/lib/tool-kit/calculus/integrate";
import { autoWindow } from "@/lib/tool-kit/calculus/plot";
import { toLatex } from "@/lib/tool-kit/calculus/print";
import { CHART_COLORS } from "@/lib/tool-kit/palette";

type Strings = typeof DEFINITE_INTEGRAL_CALCULATOR_STRINGS;
type Fmt = ReturnType<typeof useToolNumber>;
type OkView = Extract<IntegralView, { kind: "ok" }>;

const SAMPLES = 300;

/** Ten significant digits; a value no larger than its own error estimate reads as 0. */
function digits(fmt: Fmt, value: number, strings: Strings, error = 0): string {
  if (!Number.isFinite(value)) {
    return strings.noValue;
  }
  if (value === 0 || Math.abs(value) <= error) {
    return fmt(0);
  }
  return fmt(value, { maximumSignificantDigits: 10 });
}

function warningText(
  warning: IntegralWarning,
  strings: Strings,
  fmt: Fmt
): string {
  const template = strings.warnings[warning.code];
  switch (warning.code) {
    case "infinite-limit":
      return template;
    case "methods-disagree":
      return t(template, {
        other: fmt(warning.other, { maximumSignificantDigits: 8 }),
      });
    default:
      return t(template, {
        at: fmt(warning.at, { maximumSignificantDigits: 4 }),
      });
  }
}

/** Polygons between the curve and the axis, one colour for each sign. */
function Shading({
  f,
  from,
  to,
  scales,
}: {
  f: (x: number) => number;
  from: number;
  to: number;
  scales: PlaneScales;
}) {
  const lo = Math.max(from, scales.viewport.xMin);
  const hi = Math.min(to, scales.viewport.xMax);
  if (!(hi > lo)) {
    return null;
  }
  const paths = ([1, -1] as const).map((sign) => {
    const axis = scales.y(0);
    const commands: string[] = [];
    let run: [number, number][] = [];
    const flush = () => {
      if (run.length > 1) {
        const first = run[0];
        const last = run.at(-1) ?? first;
        commands.push(
          `M${first[0].toFixed(1)} ${axis.toFixed(1)} ${run
            .map(([px, py]) => `L${px.toFixed(1)} ${py.toFixed(1)}`)
            .join(" ")} L${last[0].toFixed(1)} ${axis.toFixed(1)} Z`
        );
      }
      run = [];
    };
    for (let i = 0; i <= SAMPLES; i += 1) {
      const x = lo + ((hi - lo) * i) / SAMPLES;
      const y = f(x);
      if (!Number.isFinite(y)) {
        flush();
        continue;
      }
      const clipped = sign === 1 ? Math.max(y, 0) : Math.min(y, 0);
      run.push([scales.x(x), Math.max(-1e4, Math.min(1e4, scales.y(clipped)))]);
    }
    flush();
    return commands.join(" ");
  });
  return (
    <>
      <path d={paths[0]} fill={CHART_COLORS[0]} fillOpacity={0.3} />
      <path d={paths[1]} fill={CHART_COLORS[3]} fillOpacity={0.3} />
    </>
  );
}

function Graph({ view, strings }: { view: OkView; strings: Strings }) {
  const { f, lower, upper } = view;
  const [xMin, xMax] = plotRange(lower.value, upper.value);
  const window = useMemo(() => {
    const base = autoWindow([f], xMin, xMax);
    const pad = (base.yMax - base.yMin) * 0.05;
    return {
      ...base,
      yMin: Math.min(base.yMin, -pad),
      yMax: Math.max(base.yMax, pad),
    };
  }, [f, xMin, xMax]);
  const lowerText = lower.ast
    ? shown(lower.ast, view.decimals)
    : `−${strings.infinity}`;
  const upperText = upper.ast
    ? shown(upper.ast, view.decimals)
    : strings.infinity;
  const windowed = !(lower.ast && upper.ast);
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.graphHeading}</h3>
      {windowed ? (
        <p className="text-muted-foreground text-sm">
          {strings.graphWindowNote}
        </p>
      ) : null}
      <FunctionGraph
        downloadPngLabel={strings.downloadPng}
        downloadSvgLabel={strings.downloadSvg}
        filename="definite-integral-graph"
        series={[
          { f, color: CHART_COLORS[0], label: strings.seriesFunction },
          {
            f: () => Number.NaN,
            color: CHART_COLORS[0],
            width: 8,
            label: strings.seriesPositive,
          },
          {
            f: () => Number.NaN,
            color: CHART_COLORS[3],
            width: 8,
            label: strings.seriesNegative,
          },
        ]}
        title={t(strings.graphTitle, { lower: lowerText, upper: upperText })}
        under={(scales) => (
          <Shading f={f} from={lower.value} scales={scales} to={upper.value} />
        )}
        window={window}
      />
    </div>
  );
}

export function IntegralResults({
  view,
  strings,
  fmt,
}: {
  view: OkView;
  strings: Strings;
  fmt: Fmt;
}) {
  const { report, lower, upper, decimals } = view;
  const { net, total } = report;
  const lowerLatex = lower.ast ? toLatex(lower.ast, { decimals }) : "-\\infty";
  const upperLatex = upper.ast ? toLatex(upper.ast, { decimals }) : "\\infty";
  const lowerText = lower.ast
    ? shown(lower.ast, decimals)
    : `−${strings.infinity}`;
  const upperText = upper.ast ? shown(upper.ast, decimals) : strings.infinity;
  const finite = Number.isFinite(net.value);
  const methodText =
    net.method === "tanh-sinh"
      ? strings.methodTanhSinh
      : t(strings.methodGaussKronrod, { intervals: net.intervals });
  const shownWarnings = net.warnings.filter(
    (warning, index, all) =>
      all.findIndex((other) => other.code === warning.code) === index
  );
  return (
    <>
      <p className="text-foreground text-sm">
        <MathView
          label={`∫ ${shown(view.ast, decimals)} dx from ${lowerText} to ${upperText}`}
          latex={`\\int_{${lowerLatex}}^{${upperLatex}} ${toLatex(view.ast, { decimals })}\\,dx`}
        />
      </p>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.valueLabel}
          value={digits(fmt, net.value, strings, net.error)}
        />
        <StatCard
          label={strings.errorLabel}
          value={
            Number.isFinite(net.error)
              ? fmt(net.error, {
                  notation: "scientific",
                  maximumSignificantDigits: 2,
                })
              : strings.noValue
          }
        />
        {total ? (
          <StatCard
            label={strings.totalLabel}
            value={digits(fmt, total.value, strings, total.error)}
          />
        ) : null}
        <StatCard
          label={strings.evaluationsLabel}
          value={fmt(net.evaluations)}
        />
      </StatGrid>
      {finite ? (
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyValue}
          small
          text={String(Number(net.value.toPrecision(12)))}
        />
      ) : null}
      {report.changesSign ? <Notice>{strings.signNote}</Notice> : null}
      {shownWarnings.map((warning) =>
        warning.code === "divergent" || warning.code === "undefined-region" ? (
          <ErrorNotice key={warning.code}>
            {warningText(warning, strings, fmt)}
          </ErrorNotice>
        ) : (
          <Notice key={warning.code}>
            {warningText(warning, strings, fmt)}
          </Notice>
        )
      )}
      <div className="space-y-1">
        <h3 className={WIDGET_H3}>{strings.methodHeading}</h3>
        <p className="text-muted-foreground text-sm">{methodText}</p>
      </div>
      <Graph strings={strings} view={view} />
    </>
  );
}
